import React, { useState, useEffect, useMemo, useCallback, useRef, useDeferredValue } from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './constants';
import { useAuth } from './AuthContext';
import { Search, Clear, UploadFile, InfoOutlined } from '@mui/icons-material';
import {
  CATALOG_COLUMNS,
  CatalogRow,
  HEAD_CELL,
  ImportDuplicatesDialog,
  ProductDetailPanel,
} from './ReferenceCatalogPanels';

const apiError = (err, fallback) => err.response?.data?.detail || err.message || fallback;

const MULTIPART = { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } };
const PANE_SX = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  height: '100%',
  overflow: 'hidden',
};
const PAPER_FILL_SX = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
};

const FILTER_FIELDS = [
  { key: 'category', label: 'Category', stateKey: 'selectedCategory' },
  { key: 'manufacturer', label: 'Manufacturer', stateKey: 'selectedManufacturer' },
  { key: 'brand', label: 'Brand', stateKey: 'selectedBrand' },
];

const IMPORT_CSV_HELP = (
  <Box sx={{ maxWidth: 320 }}>
    <Typography variant="caption" display="block" fontWeight={600} gutterBottom>
      Required columns
    </Typography>
    <Typography variant="caption" display="block" component="span">
      ean, name (or Title), brand, category
    </Typography>
    <Typography variant="caption" display="block" fontWeight={600} sx={{ mt: 1 }} gutterBottom>
      Optional (common)
    </Typography>
    <Typography variant="caption" display="block" component="span">
      Manufacturer, model series, Url, Image, catalog_id, status / new,
      portions_in_package, features, and any other columns → stored in spec
    </Typography>
  </Box>
);

function postMultipart(path, file, fields = {}) {
  const formData = new FormData();
  formData.append('file', file);
  Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
  return axios.post(`${API_URL}${path}`, formData, MULTIPART);
}

function ReferenceCatalogView() {
  const { projectKey } = useParams();
  const { isAdmin } = useAuth();
  const canAccess = isAdmin || projectKey === 'matching';

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filters, setFilters] = useState({
    selectedBrand: '',
    selectedManufacturer: '',
    selectedCategory: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [selectedEan, setSelectedEan] = useState(null);

  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailCacheRef = useRef(new Map());

  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const pendingImportFileRef = useRef(null);
  const fileInputRef = useRef(null);

  const { selectedBrand, selectedManufacturer, selectedCategory } = filters;
  const setFilter = (stateKey, value) => {
    setFilters((prev) => ({ ...prev, [stateKey]: value }));
  };

  const loadCatalog = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/reference/catalog`);
      setData(response.data.rows || []);
      detailCacheRef.current.clear();
      setSelectedDetail(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(apiError(err, 'Failed to load product reference catalog'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!selectedEan) {
      setSelectedDetail(null);
      setDetailLoading(false);
      return undefined;
    }

    const cached = detailCacheRef.current.get(selectedEan);
    if (cached) {
      setSelectedDetail(cached);
      setDetailLoading(false);
      return undefined;
    }

    setSelectedDetail(null);
    let cancelled = false;
    setDetailLoading(true);
    axios
      .get(`${API_URL}/reference/catalog/${encodeURIComponent(selectedEan)}`)
      .then((response) => {
        if (cancelled) return;
        const row = response.data.row;
        detailCacheRef.current.set(selectedEan, row);
        setSelectedDetail(row);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching product detail:', err);
        setSelectedDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedEan]);

  const clearPendingImport = () => {
    pendingImportFileRef.current = null;
    setImportPreview(null);
  };

  const runImport = useCallback(async (file, onDuplicate) => {
    setImporting(true);
    setImportMessage(null);
    try {
      const { data: result } = await postMultipart('/reference/catalog/import', file, {
        on_duplicate: onDuplicate,
      });
      const msg =
        `Import done: ${result.inserted} inserted, ${result.updated} updated` +
        (result.skipped ? `, ${result.skipped} skipped` : '') +
        (result.failed ? `, ${result.failed} failed` : '');
      setImportMessage({
        severity: result.failed ? 'warning' : 'success',
        text: result.errors?.length ? `${msg}. ${result.errors.join('; ')}` : msg,
      });
      await loadCatalog({ silent: true });
    } catch (err) {
      setImportMessage({
        severity: 'error',
        text: apiError(err, 'Import failed'),
      });
    } finally {
      setImporting(false);
      clearPendingImport();
    }
  }, [loadCatalog]);

  const handleImportCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setImporting(true);
      setImportMessage(null);
      const { data: preview } = await postMultipart('/reference/catalog/import/preview', file);

      if (!preview.has_duplicates) {
        await runImport(file, 'overwrite');
        return;
      }

      pendingImportFileRef.current = file;
      setImportPreview(preview);
      setImporting(false);
    } catch (err) {
      setImportMessage({
        severity: 'error',
        text: apiError(err, 'Import preview failed'),
      });
      setImporting(false);
    }
  };

  const closeImportDialog = () => {
    if (!importing) clearPendingImport();
  };

  const confirmImport = (onDuplicate) => {
    const file = pendingImportFileRef.current;
    if (!file) {
      clearPendingImport();
      return;
    }
    runImport(file, onDuplicate);
  };

  const filterOptions = useMemo(() => {
    const sets = { brand: new Set(), manufacturer: new Set(), category: new Set() };
    for (const item of data) {
      if (item.brand) sets.brand.add(item.brand);
      if (item.manufacturer) sets.manufacturer.add(item.manufacturer);
      if (item.category) sets.category.add(item.category);
    }
    return {
      brand: [...sets.brand].sort(),
      manufacturer: [...sets.manufacturer].sort(),
      category: [...sets.category].sort(),
    };
  }, [data]);

  const filteredRows = useMemo(() => {
    const q = deferredSearchTerm.trim().toLowerCase();
    return data.filter((row) => {
      if (selectedBrand && row.brand !== selectedBrand) return false;
      if (selectedManufacturer && row.manufacturer !== selectedManufacturer) return false;
      if (selectedCategory && row.category !== selectedCategory) return false;
      if (!q) return true;
      return [row.Title, row.EAN, row.brand, row.manufacturer, row.category]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [data, selectedBrand, selectedManufacturer, selectedCategory, deferredSearchTerm]);

  const activeFilterCount = [
    selectedBrand,
    selectedManufacturer,
    selectedCategory,
    searchTerm.trim(),
  ].filter(Boolean).length;
  const isSearchStale = searchTerm !== deferredSearchTerm;

  useEffect(() => {
    if (selectedEan && !filteredRows.some((row) => row.EAN === selectedEan)) {
      setSelectedEan(null);
    }
  }, [filteredRows, selectedEan]);

  const clearAllFilters = () => {
    setFilters({ selectedBrand: '', selectedManufacturer: '', selectedCategory: '' });
    setSearchTerm('');
  };

  if (!canAccess) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Reference catalog requires admin access or the matching project password.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={2} sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ mb: 1.5, flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Reference Catalog
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.length} in database · {filteredRows.length} shown
            {isSearchStale ? ' · filtering…' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            hidden
            onChange={handleImportCsv}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={importing ? <CircularProgress size={14} /> : <UploadFile />}
            disabled={importing}
            onClick={() => fileInputRef.current?.click()}
          >
            Import CSV
          </Button>
          <Tooltip title={IMPORT_CSV_HELP} arrow placement="bottom-end">
            <IconButton size="small" aria-label="CSV column requirements" sx={{ color: 'text.secondary' }}>
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {importMessage && (
        <Alert severity={importMessage.severity} sx={{ mb: 1.5, flexShrink: 0 }} onClose={() => setImportMessage(null)}>
          {importMessage.text}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            userSelect: 'none',
          }}
          onClick={() => setFiltersExpanded((v) => !v)}
        >
          <IconButton size="small" tabIndex={-1}>
            {filtersExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Filters
          </Typography>
          {activeFilterCount > 0 && (
            <>
              <Chip label={`${activeFilterCount} active`} size="small" color="primary" variant="outlined" />
              <Button
                size="small"
                startIcon={<Clear fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  clearAllFilters();
                }}
              >
                Clear
              </Button>
            </>
          )}
        </Box>
        <Collapse in={filtersExpanded}>
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
              {FILTER_FIELDS.map(({ key, label, stateKey }) => (
                <FormControl key={key} size="small" sx={{ minWidth: 140, flex: 1 }}>
                  <InputLabel>{label}</InputLabel>
                  <Select
                    value={filters[stateKey]}
                    label={label}
                    onChange={(e) => setFilter(stateKey, e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {filterOptions[key].map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
              <TextField
                size="small"
                placeholder="Search title, EAN…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: 180, flex: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Box>
        </Collapse>
      </Paper>

      <Grid container spacing={1.5} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Grid item xs={12} md={7} sx={PANE_SX}>
          <Paper variant="outlined" sx={PAPER_FILL_SX}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2">Catalog rows</Typography>
            </Box>
            {filteredRows.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Alert severity="info" sx={{ py: 0.5 }}>No rows match current filters.</Alert>
              </Box>
            ) : (
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      {CATALOG_COLUMNS.map((col) => (
                        <TableCell key={col.key} sx={{ ...HEAD_CELL, width: col.width }}>
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row, idx) => (
                      <CatalogRow
                        key={row.EAN || idx}
                        row={row}
                        selected={selectedEan === row.EAN}
                        onSelect={setSelectedEan}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5} sx={PANE_SX}>
          <Paper variant="outlined" sx={PAPER_FILL_SX}>
            <ProductDetailPanel product={selectedDetail} loading={detailLoading && !!selectedEan} />
          </Paper>
        </Grid>
      </Grid>

      <ImportDuplicatesDialog
        preview={importPreview}
        importing={importing}
        onClose={closeImportDialog}
        onSkip={() => confirmImport('skip')}
        onOverwrite={() => confirmImport('overwrite')}
      />
    </Box>
  );
}

export default ReferenceCatalogView;
